import { useRef, useState } from 'react'
import helper from '../../utils/helper';
import XLSX from "xlsx";
import { Button, Col, Descriptions, message, Result, Row, Spin } from 'antd';
import { UploadOutlined, FrownOutlined } from '@ant-design/icons';
import { BulkActionSheetTemplate as SheetTemplate} from '@operations/shared';
import { bulkUploadCompanyBranchesTemplate } from '../../utils/bulkSheetsTemplates';
import { DataTable } from '@operations/shared';
import { createCompanyBranchesApi } from '../../utils/apisConsts';
import { request } from '../../../../shared/src/apis';
import { useDispatch, useSelector } from 'react-redux';
import { errorMessage } from '../../utils/commonStringsConsts';
import { stepperMoving } from '../../stores/actions';
import { ModalComponent } from '@operations/shared';

const tableColumns = [
    {
        title: 'Branch Name',
        dataIndex: 'branchName',
        width: 150
    },
    {
        title: 'Branch Name in Arabic',
        dataIndex: 'branchNameAr',
        width: 150
    }
]

function BulkUploadBranchesSheet() {
    const dispatch = useDispatch();
    const companyId = useSelector((state: any) => state.actionsReducer.info?.id);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [validationErrors, setValidationErrors] = useState<string>();
    const [validFiles, setValidFiles] = useState(false);
    const [dataSheet, setDataSheet] = useState<any[]>([]);
    const [pagination, setPagination] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [errorReasons, setErrorReasons] = useState<string[]>();

    const validateData = (data: { [key: string]: any }[]) => {
        const validationRegex: { [key: string]: any } = {
            "branchName": helper.regexValidation.englishRegex,
            "branchNameAr": helper.regexValidation.arabicRegex,
        }
        if (!data.length) {
            setValidationErrors("The file must not be empty");
            return;
        }

        setValidFiles(false);
        for (let index = 0; index < data.length; index++) {
            const row = index + 2
            // Check for missing columns
            const missingCols = Object.keys(validationRegex).filter((key) => !Object.keys(data[index]).includes(key));
            // Check for empty/required columns (present but empty or null/undefined)
            const emptyRequiredCols = Object.keys(validationRegex).filter((key) => {
                return (data[index][key] === undefined || data[index][key] === null || data[index][key] === "");
            });
            if (missingCols.length > 0) {
                setDataSheet([]);
                setValidationErrors(`Missing required columns in the file: ${missingCols.join(", ")}`);
                return;
            }
            if (emptyRequiredCols.length > 0) {
                setDataSheet([]);
                setValidationErrors(`In row ${row}: ${emptyRequiredCols.map((col) => `'${col}' is required`).join(", ")}`);
                return;
            }

            for (let key in data[index]) {
                if (!validationRegex[key]) {
                    setDataSheet([]);
                    setValidationErrors(`Incorrect header (${key}), Please check if this column is required and the spelling is correct`)
                    return
                }
                if (validationRegex[key] && !validationRegex[key].exec(data[index][key])) {
                    setDataSheet([]);
                    setValidationErrors(`Data format is incorrect (${key}) in row (${index + 2})`)
                    return
                }
            }
        }

        setDataSheet(data);
    };

    const handleFile = (file: any) => {
        let file_size = file.size / (1024 * 1024), maxSize = 12;
        // /* Boilerplate to set up FileReader */
        let acceptable_exe = helper.regexValidation.xlsxCsvRegex;
        if (!acceptable_exe.exec(file.name)) {
            setValidFiles(false);
            message.error(`The extension of (${file.name}) is not supported!`);
        } else if (file_size > maxSize) {
            setValidFiles(false);
            message.error(`The size of (${file.name}) is too large!`);

        } else {
            setValidFiles(true);
            let fileReader = new FileReader(), rowObject: any, result, workbook: XLSX.WorkBook;
            fileReader.readAsBinaryString(file);
            fileReader.onload = (event: any) => {

                result = event.target.result;
                workbook = XLSX.read(result, { type: "binary", cellDates: true });
                workbook.SheetNames.forEach(sheet => {
                    rowObject = helper.sheetToJson(workbook.Sheets[sheet]);
                });
                validateData(rowObject);
            }
        }
    }

    const handleFileChange = (e: { target: { files: any, value: any }; }) => {
        const files = e.target.files;
        setValidationErrors("");
        if (files && files[0]) handleFile(files[0]);
        e.target.value = null;
    }

    const handleTablePagination = (e: any) => {
        setPagination({ pagination: e })
        setLoading(true)
        setTimeout(() => setLoading(false), 1500)
    }

    const handleSubmitBranches = async () => {
        setSubmitLoading(true);
        try {
            const response: any = await dispatch(request({ url: createCompanyBranchesApi + companyId, method: "POST", data: { sheetData: dataSheet } }))
            if (response?.data?.status === 200) {
                message.success(response?.data?.message)
                dispatch(stepperMoving(0))
            } else if (response?.data?.status === 400) {
                if (response?.data?.reasons) {
                    setErrorReasons(response?.data?.reasons?.flatMap((reason: any) => reason?.errors))
                    setShowModal(true)
                } else message.error(response?.data?.message)
            } else {
                message.error(errorMessage)
            }
            setSubmitLoading(false);
        } catch (error: any) {
            setSubmitLoading(false);
            if (error?.response?.data?.status === 400) {
                if (error?.response?.data?.reasons) {
                    setErrorReasons(error?.response?.data?.reasons?.flatMap((reason: any) => reason?.errors))
                    setShowModal(true)
                } else message.error(error?.response?.data?.message)
            } else {
                message.error(errorMessage)
            }
        }
    }

    const generateErrorsTable = () => {
        const rows = errorReasons && Array.isArray(errorReasons) &&
            errorReasons?.map((errorRow: string) => <Descriptions.Item span={4}>{errorRow}</Descriptions.Item>)
        return <>
            <Result
                status="error"
                style={{ padding: "30px" }}
                title={<p style={{ fontSize: '18px', padding: 0 }}>Error while adding a new company branch</p>}
            />
            <Descriptions bordered>
                <Descriptions.Item span={3}><b>Errors Descriptions</b></Descriptions.Item>
                {rows}
            </Descriptions>
        </>
    }

    return (
        <>
            <input
                hidden
                type="file"
                className="form-control"
                id="file"
                ref={fileInputRef}
                accept=".csv,.xlsx"
                onChange={handleFileChange}
            />
            <Row style={{ marginTop: "20px" }}>
                <Col span={5}><Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()} >Click to Upload file</Button></Col>
                {dataSheet?.length > 0 && !validFiles && <Col span={19}> <Button type="primary" onClick={handleSubmitBranches} loading={submitLoading} style={{ float: 'right' }}>
                    Submit File
                </Button>  </Col>}
                {dataSheet.length === 0 && <Col>
                    <p>Please Upload your file as shown in the following table, Only the following data will be sent</p>
                </Col>}
            </Row>
            {
                !validFiles && dataSheet.length > 0 ?
                    <Row style={{ marginTop: "20px", maxWidth: "1300px" }}>
                        <label style={{ fontSize: "20px", fontWeight: "bold" }}>Branches Data</label>
                        <DataTable
                            columns={tableColumns}
                            pagination={pagination}
                            rows={dataSheet}
                            handleTableChange={handleTablePagination}
                            loading={loading}
                            scrolling={{ x: 1500, y: 300 }} />
                    </Row>
                    : <SheetTemplate columns={bulkUploadCompanyBranchesTemplate} />
            }
            {
                (validationErrors || loading) && <Result
                    icon={loading ? <Spin size="large" /> : <FrownOutlined />}
                    title={loading ? "Please wait, your file is uploading now" : validationErrors}
                />
            }
            <ModalComponent
                content={generateErrorsTable()}
                visibleModal={showModal}
                handleCloseModal={() => setShowModal(false)}
                handleAcceptModal={() => setShowModal(false)}
                cancelText="Close"
                okText="Upload again"
            />
        </>
    )
}

export default BulkUploadBranchesSheet
